
import React, { useRef, useEffect } from 'react';
import { StarType } from '../types';
import StarIconEmpty from './icons/stars/StarIconEmpty';
import YellowStarIcon from './icons/stars/YellowStarIcon';
import RedExclamationIcon from './icons/stars/RedExclamationIcon';
import BlueFlagIcon from './icons/stars/BlueFlagIcon';
import GreenTickIcon from './icons/stars/GreenTickIcon';
import PurplePinIcon from './icons/stars/PurplePinIcon';
import OrangeDiamondIcon from './icons/stars/OrangeDiamondIcon';
// Import new icons
import PinkStarIcon from './icons/stars/PinkStarIcon';
import LightBlueStarIcon from './icons/stars/LightBlueStarIcon';
import TealStarIcon from './icons/stars/TealStarIcon';
import LimeStarIcon from './icons/stars/LimeStarIcon';
import AmberStarIcon from './icons/stars/AmberStarIcon';
import RedStarIcon from './icons/stars/RedStarIcon';
import GreyStarIcon from './icons/stars/GreyStarIcon';
import PurpleQuestionIcon from './icons/stars/PurpleQuestionIcon';
import CyanInfoIcon from './icons/stars/CyanInfoIcon';
import GreenArrowRightIcon from './icons/stars/GreenArrowRightIcon';
import BlueArrowLeftIcon from './icons/stars/BlueArrowLeftIcon';
import OrangeDoubleExclamationIcon from './icons/stars/OrangeDoubleExclamationIcon';
import IndigoPinIcon from './icons/stars/IndigoPinIcon';


interface StarPickerProps {
  currentStar: StarType;
  onSelectStar: (star: StarType) => void;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>; 
}

const starIconsMap: Record<StarType, { icon: React.FC<React.SVGProps<SVGSVGElement>>; color: string; label: string }> = {
  [StarType.None]: { icon: StarIconEmpty, color: 'text-gray-400 hover:text-gray-200', label: 'Sin marca' },
  [StarType.YellowStar]: { icon: YellowStarIcon, color: 'text-yellow-400 hover:text-yellow-300', label: 'Estrella Amarilla' },
  [StarType.RedExclamation]: { icon: RedExclamationIcon, color: 'text-red-500 hover:text-red-400', label: 'Exclamación Roja' },
  [StarType.BlueFlag]: { icon: BlueFlagIcon, color: 'text-blue-500 hover:text-blue-400', label: 'Bandera Azul' },
  [StarType.GreenTick]: { icon: GreenTickIcon, color: 'text-green-500 hover:text-green-400', label: 'Tick Verde' },
  [StarType.PurplePin]: { icon: PurplePinIcon, color: 'text-purple-500 hover:text-purple-400', label: 'Chincheta Púrpura' },
  [StarType.OrangeDiamond]: { icon: OrangeDiamondIcon, color: 'text-orange-500 hover:text-orange-400', label: 'Diamante Naranja' },
  
  // New icons added here
  [StarType.PinkStar]: { icon: PinkStarIcon, color: 'text-pink-500 hover:text-pink-400', label: 'Estrella Rosa' },
  [StarType.LightBlueStar]: { icon: LightBlueStarIcon, color: 'text-sky-500 hover:text-sky-400', label: 'Estrella Azul Claro' },
  [StarType.TealStar]: { icon: TealStarIcon, color: 'text-teal-500 hover:text-teal-400', label: 'Estrella Turquesa' },
  [StarType.LimeStar]: { icon: LimeStarIcon, color: 'text-lime-500 hover:text-lime-400', label: 'Estrella Lima' },
  [StarType.AmberStar]: { icon: AmberStarIcon, color: 'text-amber-500 hover:text-amber-400', label: 'Estrella Ámbar' },
  [StarType.RedStar]: { icon: RedStarIcon, color: 'text-red-600 hover:text-red-500', label: 'Estrella Roja' },
  [StarType.GreyStar]: { icon: GreyStarIcon, color: 'text-gray-500 hover:text-gray-400', label: 'Estrella Gris' },
  [StarType.PurpleQuestion]: { icon: PurpleQuestionIcon, color: 'text-purple-600 hover:text-purple-500', label: 'Pregunta Púrpura' },
  [StarType.CyanInfo]: { icon: CyanInfoIcon, color: 'text-cyan-500 hover:text-cyan-400', label: 'Info Cian' },
  [StarType.GreenArrowRight]: { icon: GreenArrowRightIcon, color: 'text-green-600 hover:text-green-500', label: 'Flecha Verde Derecha' },
  [StarType.BlueArrowLeft]: { icon: BlueArrowLeftIcon, color: 'text-blue-600 hover:text-blue-500', label: 'Flecha Azul Izquierda' },
  [StarType.OrangeDoubleExclamation]: { icon: OrangeDoubleExclamationIcon, color: 'text-orange-600 hover:text-orange-500', label: 'Doble Exclamación Naranja' },
  [StarType.IndigoPin]: { icon: IndigoPinIcon, color: 'text-indigo-500 hover:text-indigo-400', label: 'Chincheta Índigo' },
};


const StarPicker: React.FC<StarPickerProps> = ({ currentStar, onSelectStar, onClose, triggerRef }) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
          triggerRef?.current && !triggerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, triggerRef]);

  const starOptions = Object.values(StarType);

  return (
    <div
      ref={pickerRef}
      className="absolute z-50 mt-0.5 bg-gray-800 border border-gray-700 rounded-md shadow-lg p-2"
      style={{
        top: '0px', 
        right: 'calc(100% + 4px)',
        minWidth: '160px', // Adjusted for 5 columns
      }}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="star-picker-button"
    >
      <div className="grid grid-cols-5 gap-1.5"> {/* Changed to grid-cols-5 and reduced gap */}
        {starOptions.map(starType => {
          const starInfo = starIconsMap[starType];
          if (!starInfo) { // Should not happen if map is complete
            console.warn(`Star type ${starType} not found in starIconsMap.`);
            return null;
          }
          const { icon: IconComponent, color, label } = starInfo;
          const isSelected = starType === currentStar;
          return (
            <button
              key={starType}
              onClick={() => {
                onSelectStar(starType);
                onClose();
              }}
              className={`p-1.5 rounded-md flex items-center justify-center transition-colors ${color} ${isSelected ? 'bg-gray-700 ring-2 ring-indigo-500' : 'hover:bg-gray-700'}`}
              title={label}
              role="menuitem"
              aria-label={label}
            >
              <IconComponent className="w-5 h-5" /> {/* Standardized icon size */}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StarPicker;
export { starIconsMap };
